import { type FormEventHandler, type ReactNode, useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import CheckboxInputListItem from 'LuxKit/List/CheckboxInputListItem'
import FilterPanelCheckboxGroup, {
  type FilterPanelCheckItem,
} from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import FilterPanelFilterGroup from 'components/Common/FilterPanel/FilterPanelFilterGroup'
import LoyaltyTierLabel from 'components/Common/Labels/LoyaltyTierLabel'
import { useAppSelector } from 'hooks/reduxHooks'
import LoyaltyInfiniteLogo from 'loyalty/components/LoyaltyInfiniteLogo'
import LoyaltyPointsSliderSearch from 'loyalty/components/LoyaltyPointsSliderSearch'
import getLoyaltyIsInfiniteAccountTier from 'loyalty/selectors/getLoyaltyIsInfiniteAccountTier'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import useSearchBurnEnabled from 'hooks/useSearchBurnEnabled'

const FILTER_GROUP_GAP = 8

const FilterPanelCheckboxSpacing = styled.div`
  > * {
    margin-top: ${rem(FILTER_GROUP_GAP)};
  }
`

const BurnControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(FILTER_GROUP_GAP)};
`

const Children = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(FILTER_GROUP_GAP)};
  margin-top: ${rem(FILTER_GROUP_GAP)};

  > * {
    margin-top: 0;
  }
`

const SliderContainer = styled.div`
  min-width: 0;
`

const ViewPriceInPointsCheckbox = styled(CheckboxInputListItem)`
  &&& {
    background: transparent;
    padding: 0;
  }
`

const InfiniteFilterLogo = styled(LoyaltyInfiniteLogo)`
  display: block;
  height: ${rem(8)};
  width: auto;
`

interface Props {
  cacheKey?: string
  skipBatchMaxCap?: boolean
  skipBurnExperiment?: boolean
  viewPriceInPoints: boolean
  onViewPriceInPointsChange: (viewPriceInPoints: boolean) => void
  compact?: boolean
  loading?: boolean
  enableBurnControls?: boolean
  onBurnControlsChange?: FormEventHandler<HTMLDivElement>
  children?: ReactNode
}

function LoyaltySocietePointsFilterGroup({
  cacheKey,
  skipBatchMaxCap,
  skipBurnExperiment,
  viewPriceInPoints,
  onViewPriceInPointsChange,
  compact,
  loading,
  enableBurnControls = false,
  onBurnControlsChange,
  children,
}: Props) {
  const intl = useIntl()
  const isInfiniteTier = useAppSelector(getLoyaltyIsInfiniteAccountTier)
  const { setSearchBurnEnabled, setPointsToBurn } = useSearchBurnEnabled()
  const loyaltyProgramName = getLoyaltyProgramDisplayName()

  const clearViewPriceInPoints = useCallback(() => {
    onViewPriceInPointsChange(false)
  }, [onViewPriceInPointsChange])

  const handleViewPriceInPointsToggle = useCallback(() => {
    if (viewPriceInPoints) {
      clearViewPriceInPoints()
    } else {
      setSearchBurnEnabled(false)
      setPointsToBurn(undefined)
      onViewPriceInPointsChange(true)
    }
  }, [viewPriceInPoints, clearViewPriceInPoints, onViewPriceInPointsChange, setSearchBurnEnabled, setPointsToBurn])

  const viewPriceInPointsLabel = intl.formatMessage({
    id: 'Pl7ew0Wylbx4ZUv7DdgF',
    defaultMessage: 'View Price in Points',
  })

  const viewPriceInPointsItems = useMemo<Array<FilterPanelCheckItem>>(() => [{
    value: 'view-price-in-points',
    checked: viewPriceInPoints,
    label: viewPriceInPointsLabel,
  }], [viewPriceInPoints, viewPriceInPointsLabel])

  const loyaltyFilterTitle = useMemo(() => {
    return (
      <LoyaltyTierLabel kind="primary">
        {isInfiniteTier && <InfiniteFilterLogo role="img" aria-label={loyaltyProgramName} />}
        {!isInfiniteTier && loyaltyProgramName}
      </LoyaltyTierLabel>
    )
  }, [isInfiniteTier, loyaltyProgramName])

  return (
    <FilterPanelFilterGroup
      compact={compact}
      loading={loading}
      title={loyaltyFilterTitle}
    >
      <>
        {enableBurnControls && (
          <BurnControls data-testid="loyalty-points-filter-burn-controls" onChange={onBurnControlsChange}>
            <SliderContainer>
              <LoyaltyPointsSliderSearch
                layout="filterPanel"
                cacheKey={cacheKey}
                skipBatchMaxCap={skipBatchMaxCap}
                skipBurnExperiment={skipBurnExperiment}
                onBurnEnabled={clearViewPriceInPoints}
                disabled={viewPriceInPoints}
              />
            </SliderContainer>
            {compact && (
              <FilterPanelCheckboxSpacing data-testid="view-price-in-points-spacing">
                <FilterPanelCheckboxGroup
                  name="viewPriceInPoints"
                  items={viewPriceInPointsItems}
                  onChange={handleViewPriceInPointsToggle}
                />
              </FilterPanelCheckboxSpacing>
            )}
            {!compact && (
              <ViewPriceInPointsCheckbox
                inputPlacement="start"
                checked={viewPriceInPoints}
                onChange={handleViewPriceInPointsToggle}
                title={viewPriceInPointsLabel}
                size="medium"
              />
            )}
          </BurnControls>
        )}
        {!!children && (
          <Children data-testid="loyalty-points-filter-children">
            {children}
          </Children>
        )}
      </>
    </FilterPanelFilterGroup>
  )
}

export default LoyaltySocietePointsFilterGroup
